import { useEffect, useMemo, useState } from 'react';
import { trpc } from '@web/lib/trpc';

import { toast } from 'sonner';

import { sameItems } from '@shared/utils';
import { cn } from '@web/lib/shadcn';

import { Save, Undo } from 'lucide-react';

import { Button } from '@web/components/ui/button';
import { Combobox } from '@web/components/ui/combobox';

export function ContactProducts({ contactId }: { contactId: string }) {
  const { data: allProductsData } = trpc.product.listAll.useQuery();

  const { data: contactProducts, refetch: refetchContactProducts } =
    trpc.product.listContactProducts.useQuery({
      contactId,
    });

  const { mutateAsync: setProductsAssociatedToContact } =
    trpc.product.setProductsAssociatedToContact.useMutation();

  const contactProductsNames = useMemo(
    () => contactProducts?.map((p) => p.displayName),
    [contactProducts],
  );

  const [tmpContactProducts, setTmpContactProducts] = useState<string[]>([]);

  const resetTmpContactProducts = () => {
    if (contactProductsNames) setTmpContactProducts(contactProductsNames);
  };

  const handleUpdateProductsAssociatedToContact = async (
    products: string[],
  ) => {
    const productIds = products
      .map(
        (productName) =>
          allProductsData?.find((p) => p.displayName === productName)?.id,
      )
      .filter((id) => id !== undefined);

    await setProductsAssociatedToContact({
      contactId,
      productIds,
      relationType: 'selected',
    });

    await refetchContactProducts();

    toast.success('Associtated products updated');
  };

  useEffect(() => {
    if (contactProductsNames && contactProductsNames.length > 0)
      setTmpContactProducts((tmp) =>
        tmp.length === 0 ? contactProductsNames : tmp,
      );
  }, [contactProductsNames]);

  return (
    <div>
      <div className="mb-2 ml-2 flex items-center justify-between gap-4">
        <h3 className="my-2">Products</h3>

        <span
          className={cn(
            'flex items-center gap-0',
            !contactProductsNames ||
              (sameItems(tmpContactProducts, contactProductsNames) && 'hidden'),
          )}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void handleUpdateProductsAssociatedToContact(tmpContactProducts);
            }}
          >
            <Save className="size-4 stroke-green-700" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              resetTmpContactProducts();
            }}
          >
            <Undo className="stroke-destructive size-4" />
          </Button>
        </span>
      </div>

      <Combobox
        items={allProductsData?.map((p) => p.displayName) ?? []}
        stateManager={[tmpContactProducts, setTmpContactProducts]}
        className="w-full"
      />
    </div>
  );
}
